/**
 * The session's context and its hook, kept out of `AuthProvider.tsx`.
 *
 * Not a stylistic split. React Fast Refresh only preserves state for a module whose
 * exports are *all* components; a module exporting both `AuthProvider` and `useAuth`
 * is invalidated wholesale on every edit, which re-evaluates `createContext()` and hands
 * consumers a different context object than the mounted provider is publishing to. The
 * symptom is a blank screen and `useAuth must be used inside <AuthProvider>` on a file
 * save — observed, not theorised.
 */

import { createContext, useContext } from 'react';

import type { Fault } from './diagnose';
import type { readTenantClaim, TokenClaims } from './token';

export type AuthStatus =
  /** Reading storage, or handling the callback. */
  | 'booting'
  /** On the way to the issuer. */
  | 'redirecting'
  /** Manual mode: no issuer, waiting for a token. */
  | 'awaiting_token'
  /**
   * A token is held. This does NOT mean signed in — whether the SERVICE accepts it is a
   * separate question, answered by the first call to /v1/organizations/me. Conflating the
   * two is how a shell ends up looping against a service that was never going to accept
   * anything.
   */
  | 'authenticated'
  /** Something is wrong that another trip to the issuer cannot fix. */
  | 'blocked';

export interface AuthValue {
  status: AuthStatus;
  fault: Fault | null;
  token: string | null;
  claims: TokenClaims | null;
  /** What the shell reads out of the token, for diagnostics only. */
  tenantClaim: ReturnType<typeof readTenantClaim>;
  /** Go to the issuer, unless the loop guard says we have been here too often. */
  signIn: (returnTo?: string) => void;
  /** Drop the token and, in redirect mode, tell the issuer too. */
  signOut: () => void;
  /** Manual mode, and the callback route. */
  acceptToken: (token: string) => void;
  /** Report a failure the session should react to. Idempotent. */
  reportFault: (fault: Fault) => void;
  /** The service answered as this tenant. Clears the loop guard. */
  confirmSession: () => void;
  clearFault: () => void;
}

export const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}
